import React from 'react';
import { useOutletContext } from 'react-router-dom';
import { Heart, MessageCircle, Home, CheckCircle, AlertCircle } from 'lucide-react';
import { HolisticStudent } from '../../../types';

const ParentTab: React.FC = () => {
  const { student } = useOutletContext<{ student: HolisticStudent }>();
  const pf = student.parentFeedback;

  const supportLevel = pf.parent_support_t2.toLowerCase();
  const isHighInvolvement = supportLevel.includes('very') || supportLevel.includes('highly') || supportLevel.includes('continued');
  const isLow = supportLevel.includes('low') || supportLevel.includes('no change');

  return (
    <div className="space-y-6">
      {/* Insight Banner */}
      <div className={`flex items-center gap-3 p-4 rounded-xl border ${isHighInvolvement ? 'bg-emerald-50 border-emerald-200' : isLow ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
        {isHighInvolvement ? <CheckCircle size={18} className="text-emerald-600" /> : <AlertCircle size={18} className={isLow ? 'text-red-600' : 'text-amber-600'} />}
        <span className={`text-sm font-medium ${isHighInvolvement ? 'text-emerald-700' : isLow ? 'text-red-700' : 'text-amber-700'}`}>
          {isHighInvolvement ? 'High parent involvement detected' : isLow ? 'Low parent engagement — follow-up recommended' : 'Moderate parent engagement'}
        </span>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Parent Support */}
        <FeedbackCard
          icon={<Heart size={18} className="text-rose-500" />}
          title="Parent Support"
          items={[
            { term: 'Term 1', text: pf.parent_support_t1 },
            { term: 'Term 2', text: pf.parent_support_t2 },
          ]}
        />
        {/* Parent Engagement */}
        <FeedbackCard
          icon={<MessageCircle size={18} className="text-blue-500" />}
          title="Parent Engagement"
          items={[
            { term: 'Term 1', text: pf.parent_share_t1 },
            { term: 'Term 2', text: pf.parent_share_t2 },
          ]}
        />
        {/* Home Learning */}
        <FeedbackCard
          icon={<Home size={18} className="text-purple-500" />}
          title="Home Learning Environment"
          items={[
            { term: 'Term 1', text: pf.parent_enjoys_t1 },
            { term: 'Term 2', text: pf.parent_enjoys_t2 },
          ]}
        />
      </div>

      {/* Vaccination */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Vaccination Status</h4>
        <div className="flex gap-6">
          <div>
            <span className="text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">Term 1</span>
            <p className="text-sm text-gray-600 mt-1">{pf.parent_vaccine_t1}</p>
          </div>
          <div>
            <span className="text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">Term 2</span>
            <p className="text-sm text-gray-600 mt-1">{pf.parent_vaccine_t2}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeedbackCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  items: { term: string; text: string }[];
}> = ({ icon, title, items }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
    <div className="flex items-center gap-2 mb-4">
      {icon}
      <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
    </div>
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i}>
          <span className="inline-block text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded mb-1">{item.term}</span>
          <p className="text-sm text-gray-600">{item.text}</p>
        </div>
      ))}
    </div>
  </div>
);

export default ParentTab;
